(function () {
    'use strict';

    const COOKIE_NAME = 'bestypop_store_code';
    const STORAGE_KEY = 'bestypop_store';
    const URL_PARAMETER = 'bp_store';
    const COOKIE_MAX_AGE = 60 * 60 * 24 * 90;
    const STORE_CODE_PATTERN = /^[A-Za-z0-9_-]{3,60}$/;
    const PARTNER_CODE_PATTERN = /^[A-Za-z0-9_-]*BP$/i;

    const STORE_NAME_CACHE_PREFIX =
        'bestypop_storefront_name_';

    const STORE_NAME_CACHE_MAX_AGE =
        60 * 60 * 24 * 90 * 1000;

    const LOGO_TAP_LIMIT = 3;
    const LOGO_TAP_WINDOW = 700;

    let currentStore = null;
    let checkoutStarted = false;
    let cartSyncStarted = false;

    let logoTapCount = 0;
    let logoTapTimer = null;

    function cleanStoreCode(value) {
        if (!value) return '';

        let code = String(value).trim();

        try {
            code = decodeURIComponent(code);
        } catch (error) {
            // Keep the original value if decoding fails.
        }

        code = code
            .replace(/^["']|["']$/g, '')
            .replace(/^\/discount\//i, '')
            .split('?')[0]
            .split('#')[0]
            .trim();

        return STORE_CODE_PATTERN.test(code)
            ? code
            : '';
    }

    function codesMatch(firstCode, secondCode) {
        const first = cleanStoreCode(firstCode);
        const second = cleanStoreCode(secondCode);

        if (!first || !second) {
            return false;
        }

        return (
            first.toLowerCase() ===
            second.toLowerCase()
        );
    }

    function getCookie(name) {
        const prefix = `${name}=`;
        const cookies = document.cookie.split(';');

        for (const cookieItem of cookies) {
            const cookie = cookieItem.trim();

            if (!cookie.startsWith(prefix)) {
                continue;
            }

            const rawValue =
                cookie.substring(prefix.length);

            try {
                return decodeURIComponent(rawValue);
            } catch (error) {
                return rawValue;
            }
        }

        return '';
    }

    function setCookie(name, value, maxAge) {
        const secure =
            window.location.protocol === 'https:'
                ? '; Secure'
                : '';

        document.cookie =
            `${name}=${encodeURIComponent(value)}` +
            `; Max-Age=${maxAge}` +
            '; Path=/' +
            '; SameSite=Lax' +
            secure;
    }

    function deleteCookie(name) {
        const secure =
            window.location.protocol === 'https:'
                ? '; Secure'
                : '';

        document.cookie =
            `${name}=; Max-Age=0; Path=/; SameSite=Lax` +
            secure;
    }

    function getShopRoot() {
        const root =
            window.routes?.root ||
            window.Shopify?.routes?.root ||
            '/';

        return root.endsWith('/')
            ? root
            : `${root}/`;
    }

    function getCartUpdateUrl() {
        return (
            window.routes?.cart_update_url ||
            `${getShopRoot()}cart/update.js`
        );
    }

    function getCartUrl() {
        return `${getShopRoot()}cart.js`;
    }

    function getCheckoutUrl() {
        return `${getShopRoot()}checkout`;
    }

    function readStoredStore() {
        try {
            const rawValue =
                window.localStorage.getItem(
                    STORAGE_KEY
                );

            if (!rawValue) return null;

            const parsed =
                JSON.parse(rawValue);

            if (
                !parsed ||
                typeof parsed !== 'object'
            ) {
                return null;
            }

            const code =
                cleanStoreCode(parsed.code);

            if (!code) return null;

            return {
                code,
                updated: parsed.updated || '',
                source:
                    parsed.source ||
                    'local_storage'
            };
        } catch (error) {
            return null;
        }
    }

    function saveStore(code, source) {
        const cleanCode =
            cleanStoreCode(code);

        if (!cleanCode) return null;

        const existing =
            readStoredStore();

        const sameStore =
            existing &&
            codesMatch(
                existing.code,
                cleanCode
            );

        const store = {
            code: cleanCode,
            updated:
                sameStore && existing.updated
                    ? existing.updated
                    : new Date().toISOString(),
            source: source || 'unknown'
        };

        setCookie(
            COOKIE_NAME,
            cleanCode,
            COOKIE_MAX_AGE
        );

        try {
            window.localStorage.setItem(
                STORAGE_KEY,
                JSON.stringify(store)
            );
        } catch (error) {
            // Cookie remains available if localStorage is blocked.
        }

        currentStore = store;

        return { ...store };
    }

    function removeStoredManualDiscount() {
        try {
            window.localStorage.removeItem(
                'storedDiscount'
            );
        } catch (error) {
            // Ignore localStorage access errors.
        }
    }

    function getCodeFromUrlParameter() {
        const parameters =
            new URLSearchParams(
                window.location.search
            );

        return cleanStoreCode(
            parameters.get(URL_PARAMETER)
        );
    }

    function getCodeFromDiscountPath() {
        const match =
            window.location.pathname.match(
                /\/discount\/([^/?#]+)/i
            );

        return match?.[1]
            ? cleanStoreCode(match[1])
            : '';
    }

    function getCodeFromPartnerPage() {
        const context =
            window.BestyPopPageContext || {};

        const pageType =
            String(
                context.pageType || ''
            ).toLowerCase();

        const pageHandle =
            cleanStoreCode(
                context.pageHandle
            );

        const templateSuffix =
            cleanStoreCode(
                context.pageTemplateSuffix
            );

        /*
         * Partner storefront example:
         *
         * URL:
         * /pages/dh2026bp
         *
         * Template:
         * page.dh2026bp.json
         *
         * Both values must match.
         */
        if (
            pageType === 'page' &&
            pageHandle &&
            templateSuffix &&
            PARTNER_CODE_PATTERN.test(
                templateSuffix
            ) &&
            codesMatch(
                pageHandle,
                templateSuffix
            )
        ) {
            return templateSuffix;
        }

        return '';
    }

    function getPersistentStoreCode() {
        return cleanStoreCode(
            getCookie(COOKIE_NAME)
        );
    }

    function removeStoreParameterFromUrl() {
        const url =
            new URL(window.location.href);

        if (
            !url.searchParams.has(
                URL_PARAMETER
            )
        ) {
            return;
        }

        url.searchParams.delete(
            URL_PARAMETER
        );

        window.history.replaceState(
            window.history.state,
            document.title,
            `${url.pathname}${url.search}${url.hash}`
        );
    }

    function determineCurrentStore() {
        /*
         * Priority 1:
         * Current dedicated partner storefront.
         */
        const partnerPageCode =
            getCodeFromPartnerPage();

        if (partnerPageCode) {
            removeStoredManualDiscount();

            return saveStore(
                partnerPageCode,
                'partner_page'
            );
        }

        /*
         * Priority 2:
         * Explicit code from a shared URL.
         */
        const parameterCode =
            getCodeFromUrlParameter();

        if (parameterCode) {
            removeStoredManualDiscount();

            const store = saveStore(
                parameterCode,
                'url_parameter'
            );

            removeStoreParameterFromUrl();

            return store;
        }

        /*
         * Priority 3:
         * Shopify native discount route.
         */
        const discountPathCode =
            getCodeFromDiscountPath();

        if (discountPathCode) {
            removeStoredManualDiscount();

            return saveStore(
                discountPathCode,
                'discount_path'
            );
        }

        /*
         * Priority 4:
         * 90-day first-party cookie.
         */
        const persistentCode =
            getPersistentStoreCode();

        if (persistentCode) {
            currentStore = {
                code: persistentCode,
                updated: '',
                source: 'persistent_cookie'
            };

            return { ...currentStore };
        }

        /*
         * Priority 5:
         * localStorage backup.
         */
        const storedStore =
            readStoredStore();

        if (storedStore) {
            currentStore = storedStore;

            return { ...storedStore };
        }

        return null;
    }

    function getStoreNameCacheKey(code) {
        return (
            STORE_NAME_CACHE_PREFIX +
            cleanStoreCode(code).toLowerCase()
        );
    }

    function readCachedStoreName(code) {
        const cleanCode =
            cleanStoreCode(code);

        if (!cleanCode) return '';

        try {
            const rawValue =
                window.localStorage.getItem(
                    getStoreNameCacheKey(cleanCode)
                );

            if (!rawValue) return '';

            const cached =
                JSON.parse(rawValue);

            if (
                !cached ||
                typeof cached !== 'object' ||
                typeof cached.name !== 'string'
            ) {
                return '';
            }

            const updated =
                Number(cached.updated || 0);

            if (
                !updated ||
                Date.now() - updated >
                    STORE_NAME_CACHE_MAX_AGE
            ) {
                window.localStorage.removeItem(
                    getStoreNameCacheKey(cleanCode)
                );

                return '';
            }

            return cached.name.trim();
        } catch (error) {
            return '';
        }
    }

    function saveCachedStoreName(code, name) {
        const cleanCode =
            cleanStoreCode(code);

        const cleanName =
            String(name || '').trim();

        if (!cleanCode || !cleanName) return;

        try {
            window.localStorage.setItem(
                getStoreNameCacheKey(cleanCode),
                JSON.stringify({
                    code: cleanCode,
                    name: cleanName,
                    updated: Date.now()
                })
            );
        } catch (error) {
            // Continue when localStorage is unavailable.
        }
    }

    function extractPartnerPageContext(
        pageDocument
    ) {
        if (!pageDocument) {
            return {
                pageType: '',
                pageHandle: '',
                pageTemplateSuffix: ''
            };
        }

        const scripts =
            Array.from(
                pageDocument.querySelectorAll(
                    'script'
                )
            );

        const contextScript =
            scripts.find((script) => {
                return String(
                    script.textContent || ''
                ).includes(
                    'window.BestyPopPageContext'
                );
            });

        const scriptText =
            String(
                contextScript?.textContent || ''
            );

        if (!scriptText) {
            return {
                pageType: '',
                pageHandle: '',
                pageTemplateSuffix: ''
            };
        }

        function readJsonString(propertyName) {
            const pattern =
                new RegExp(
                    propertyName +
                    '\\\\s*:\\\\s*("(?:(?:\\\\\\\\.)|[^"\\\\\\\\])*")'
                );

            const match =
                scriptText.match(pattern);

            if (!match?.[1]) {
                return '';
            }

            try {
                return JSON.parse(match[1]);
            } catch (error) {
                return '';
            }
        }

        return {
            pageType:
                String(
                    readJsonString(
                        'pageType'
                    ) || ''
                )
                    .trim()
                    .toLowerCase(),

            pageHandle:
                cleanStoreCode(
                    readJsonString(
                        'pageHandle'
                    )
                ),

            pageTemplateSuffix:
                cleanStoreCode(
                    readJsonString(
                        'pageTemplateSuffix'
                    )
                )
        };
    }

    function isValidatedPartnerPage(
        pageDocument,
        code
    ) {
        const cleanCode =
            cleanStoreCode(code);

        if (!cleanCode) {
            return false;
        }

        const context =
            extractPartnerPageContext(
                pageDocument
            );

        return Boolean(
            PARTNER_CODE_PATTERN.test(
                cleanCode
            ) &&
            context.pageType === 'page' &&
            context.pageHandle &&
            context.pageTemplateSuffix &&
            codesMatch(
                context.pageHandle,
                cleanCode
            ) &&
            codesMatch(
                context.pageTemplateSuffix,
                cleanCode
            )
        );
    }

    function extractStoreNameFromPage(
        pageDocument
    ) {
        if (!pageDocument) return '';

        const heading =
            pageDocument.querySelector(
                [
                    'main h1',
                    '.page-header h1',
                    '.page-title',
                    '.page-header-title',
                    '[data-page-title]'
                ].join(',')
            );

        const headingText =
            heading?.textContent?.trim();

        if (headingText) {
            return headingText;
        }

        const openGraphTitle =
            pageDocument
                .querySelector(
                    'meta[property="og:title"]'
                )
                ?.getAttribute('content')
                ?.trim();

        if (openGraphTitle) {
            return openGraphTitle;
        }

        const browserTitle =
            pageDocument
                .querySelector('title')
                ?.textContent
                ?.trim();

        if (!browserTitle) return '';

        return browserTitle
            .replace(
                /\s+[|–—-]\s+BestyPop.*$/i,
                ''
            )
            .trim();
    }

    async function fetchStoreName(code) {
        const cleanCode =
            cleanStoreCode(code);

        if (!cleanCode) return '';

        try {
            const existingName =
                window.BestyPopProductTitles
                    ?.getCurrentStoreName?.();

            if (existingName) {
                saveCachedStoreName(
                    cleanCode,
                    existingName
                );

                return String(
                    existingName
                ).trim();
            }
        } catch (error) {
            // Continue with cache/page lookup.
        }

        const cachedName =
            readCachedStoreName(cleanCode);

        if (cachedName) {
            return cachedName;
        }

        const context =
            window.BestyPopPageContext || {};

        const contextHandle =
            cleanStoreCode(
                context.pageHandle
            );

        const contextTemplate =
            cleanStoreCode(
                context.pageTemplateSuffix
            );

        const contextTitle =
            String(
                context.pageTitle || ''
            ).trim();

        if (
            contextTitle &&
            codesMatch(
                contextHandle,
                cleanCode
            ) &&
            codesMatch(
                contextTemplate,
                cleanCode
            )
        ) {
            saveCachedStoreName(
                cleanCode,
                contextTitle
            );

            return contextTitle;
        }

        const storefrontPath =
            `/pages/${encodeURIComponent(
                cleanCode
            )}`;

        try {
            const response = await fetch(
                storefrontPath,
                {
                    method: 'GET',
                    credentials: 'same-origin',
                    headers: {
                        Accept: 'text/html'
                    },
                    cache: 'no-store'
                }
            );

            if (!response.ok) {
                return '';
            }

            const html =
                await response.text();

            const pageDocument =
                new DOMParser()
                    .parseFromString(
                        html,
                        'text/html'
                    );

            /*
             * A discount code is treated as a real
             * storefront only when the matching Shopify
             * page exists AND that page uses the template
             * with the same code.
             *
             * Required example:
             *   Page handle: DH2033BP
             *   Template:    page.DH2033BP
             */
            if (
                !isValidatedPartnerPage(
                    pageDocument,
                    cleanCode
                )
            ) {
                console.warn(
                    'Partner code has no validated storefront page/template:',
                    cleanCode
                );

                return '';
            }

            const storeName =
                extractStoreNameFromPage(
                    pageDocument
                );

            if (storeName) {
                saveCachedStoreName(
                    cleanCode,
                    storeName
                );
            }

            return storeName;
        } catch (error) {
            console.warn(
                'Unable to resolve partner storefront name:',
                cleanCode,
                error
            );

            return '';
        }
    }

    function derivePartnerDomain(storeName) {
        const value =
            String(storeName || '')
                .trim()
                .toLowerCase();

        if (!value) return '';

        const withoutProtocol =
            value.replace(
                /^https?:\/\//,
                ''
            );

        const firstPart =
            withoutProtocol
                .split('/')[0]
                .trim();

        return (
            /^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}$/i
                .test(firstPart)
        )
            ? firstPart
            : '';
    }

    async function getPartnerCartAttributes(code) {
        const cleanCode =
            cleanStoreCode(code);

        if (!cleanCode) {
            return {};
        }

        const storeName =
            await fetchStoreName(
                cleanCode
            );

        const attributes = {
            'Partner Code': cleanCode
        };

        if (storeName) {
            attributes['Partner Store'] =
                storeName;

            attributes['Partner Storefront Valid'] =
                'true';

            const partnerDomain =
                derivePartnerDomain(
                    storeName
                );

            if (partnerDomain) {
                attributes['Partner Domain'] =
                    partnerDomain;
            }
        }

        return attributes;
    }

    function normalizeDestination(destination) {
        try {
            const url = new URL(
                destination ||
                    window.location.href,
                window.location.origin
            );

            if (
                url.origin !==
                window.location.origin
            ) {
                return '/';
            }

            return (
                `${url.pathname}` +
                `${url.search}` +
                `${url.hash}`
            );
        } catch (error) {
            return '/';
        }
    }

    function buildStoreUrl(destination, code) {
        const cleanCode =
            cleanStoreCode(code);

        const cleanDestination =
            normalizeDestination(
                destination
            );

        if (!cleanCode) {
            return (
                `${window.location.origin}` +
                `${cleanDestination}`
            );
        }

        const destinationUrl =
            new URL(
                cleanDestination,
                window.location.origin
            );

        destinationUrl.searchParams.set(
            URL_PARAMETER,
            cleanCode
        );

        const redirectTarget =
            `${destinationUrl.pathname}` +
            `${destinationUrl.search}` +
            `${destinationUrl.hash}`;

        return (
            `${window.location.origin}` +
            `/discount/${encodeURIComponent(cleanCode)}` +
            `?redirect=${encodeURIComponent(redirectTarget)}`
        );
    }

    function getPartnerHomePath(code) {
        const cleanCode =
            cleanStoreCode(code);

        if (!cleanCode) return '/';

        return (
            `/pages/${encodeURIComponent(
                cleanCode
            )}`
        );
    }

    function isExactHomepageUrl(value) {
        if (!value) return false;

        const trimmed =
            String(value).trim();

        if (
            trimmed === '#' ||
            trimmed.toLowerCase().startsWith(
                'javascript:'
            )
        ) {
            return false;
        }

        try {
            const url = new URL(
                trimmed,
                window.location.origin
            );

            if (
                url.origin !==
                window.location.origin
            ) {
                return false;
            }

            const normalizedPath =
                url.pathname.replace(
                    /\/+$/,
                    ''
                ) || '/';

            return (
                normalizedPath === '/' &&
                !url.search &&
                !url.hash
            );
        } catch (error) {
            return false;
        }
    }

    function isLogoLink(anchor) {
        if (!anchor) return false;

        const className =
            typeof anchor.className === 'string'
                ? anchor.className.toLowerCase()
                : '';

        const id =
            String(
                anchor.id || ''
            ).toLowerCase();

        const ariaLabel =
            String(
                anchor.getAttribute(
                    'aria-label'
                ) || ''
            ).toLowerCase();

        const title =
            String(
                anchor.getAttribute(
                    'title'
                ) || ''
            ).toLowerCase();

        const containsLogoImage =
            Boolean(
                anchor.querySelector(
                    'img, svg'
                )
            );

        return Boolean(
            anchor.closest(
                '.header__heading-link'
            ) ||
            anchor.closest(
                '.header-logo'
            ) ||
            anchor.closest(
                '.header__logo'
            ) ||
            anchor.closest(
                '.logo'
            ) ||
            anchor.closest(
                '[class*="logo"]'
            ) ||
            className.includes('logo') ||
            id.includes('logo') ||
            ariaLabel.includes('logo') ||
            title.includes('logo') ||
            (
                containsLogoImage &&
                anchor.closest(
                    'header, .header, .header-wrapper'
                )
            )
        );
    }

    function rewriteHomepageLinks(root) {
        const store =
            determineCurrentStore();

        const partnerCode =
            cleanStoreCode(
                store?.code
            );

        if (!partnerCode) return;

        const partnerHome =
            getPartnerHomePath(
                partnerCode
            );

        const container =
            root instanceof Element ||
            root instanceof Document
                ? root
                : document;

        const links = [];

        if (
            container instanceof Element &&
            container.matches('a[href]')
        ) {
            links.push(container);
        }

        container
            .querySelectorAll?.('a[href]')
            .forEach((anchor) => {
                links.push(anchor);
            });

        links.forEach((anchor) => {
            const href =
                anchor.getAttribute('href');

            const originalHref =
                anchor.getAttribute(
                    'data-bp-original-home-href'
                );

            const isOriginalHomepage =
                originalHref
                    ? isExactHomepageUrl(
                        originalHref
                    )
                    : isExactHomepageUrl(
                        href
                    );

            if (!isOriginalHomepage) {
                return;
            }

            if (!originalHref) {
                anchor.setAttribute(
                    'data-bp-original-home-href',
                    href || '/'
                );
            }

            anchor.setAttribute(
                'href',
                partnerHome
            );

            anchor.setAttribute(
                'data-bp-partner-home',
                partnerCode
            );

            if (isLogoLink(anchor)) {
                anchor.setAttribute(
                    'data-bp-partner-logo',
                    'true'
                );
            }
        });
    }

    function resetLogoTapState() {
        logoTapCount = 0;

        if (logoTapTimer) {
            clearTimeout(
                logoTapTimer
            );

            logoTapTimer = null;
        }
    }

    function navigateToPartnerHome(code) {
        const partnerHome =
            getPartnerHomePath(code);

        window.location.assign(
            partnerHome
        );
    }

    function navigateToMainBestyPopHome() {
        resetLogoTapState();

        /*
         * Triple-tap is the super-admin shortcut.
         * It does not clear the active storefront cookie.
         * Returning to another page will still preserve
         * the partner context.
         */
        window.location.assign(
            `${window.location.origin}/`
        );
    }

    function handleLogoTap(event) {
        const target =
            event.target instanceof Element
                ? event.target
                : null;

        if (!target) return;

        const logoLink =
            target.closest(
                'a[data-bp-partner-logo="true"]'
            );

        if (!logoLink) return;

        const store =
            determineCurrentStore();

        const partnerCode =
            cleanStoreCode(
                store?.code
            );

        if (!partnerCode) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        logoTapCount += 1;

        if (
            logoTapCount >=
            LOGO_TAP_LIMIT
        ) {
            navigateToMainBestyPopHome();
            return;
        }

        if (logoTapTimer) {
            clearTimeout(
                logoTapTimer
            );
        }

        logoTapTimer = setTimeout(
            () => {
                navigateToPartnerHome(
                    partnerCode
                );

                resetLogoTapState();
            },
            LOGO_TAP_WINDOW
        );
    }

    function installPartnerHomeNavigation() {
        rewriteHomepageLinks(
            document
        );

        /*
         * Capture mode prevents the browser from leaving
         * on the first tap while waiting to see whether
         * the super admin performs a triple-tap.
         */
        document.addEventListener(
            'click',
            handleLogoTap,
            true
        );

        /*
         * Shopify dynamically replaces sections, menus,
         * cart drawers, and headers. Rewrite any new
         * homepage links that appear later.
         */
        const observer =
            new MutationObserver(
                (mutations) => {
                    mutations.forEach(
                        (mutation) => {
                            mutation.addedNodes.forEach(
                                (node) => {
                                    if (
                                        node instanceof
                                        Element
                                    ) {
                                        rewriteHomepageLinks(
                                            node
                                        );
                                    }
                                }
                            );
                        }
                    );
                }
            );

        observer.observe(
            document.documentElement,
            {
                childList: true,
                subtree: true
            }
        );
    }

    function extractDiscountTitles(cart) {
        const titles = [];

        if (
            Array.isArray(
                cart?.cart_level_discount_applications
            )
        ) {
            cart.cart_level_discount_applications.forEach(
                (application) => {
                    if (application?.title) {
                        titles.push(
                            application.title
                        );
                    }
                }
            );
        }

        if (Array.isArray(cart?.items)) {
            cart.items.forEach((item) => {
                if (
                    Array.isArray(
                        item?.discounts
                    )
                ) {
                    item.discounts.forEach(
                        (discount) => {
                            if (discount?.title) {
                                titles.push(
                                    discount.title
                                );
                            }
                        }
                    );
                }

                if (
                    Array.isArray(
                        item?.discount_allocations
                    )
                ) {
                    item.discount_allocations.forEach(
                        (allocation) => {
                            const title =
                                allocation
                                    ?.discount_application
                                    ?.title;

                            if (title) {
                                titles.push(
                                    title
                                );
                            }
                        }
                    );
                }
            });
        }

        return titles;
    }

    function cartContainsDiscount(cart, code) {
        const cleanCode =
            cleanStoreCode(code);

        if (!cleanCode) return false;

        return extractDiscountTitles(cart).some(
            (title) =>
                codesMatch(
                    title,
                    cleanCode
                )
        );
    }

    async function getCurrentCart() {
        try {
            const response = await fetch(
                getCartUrl(),
                {
                    method: 'GET',
                    credentials: 'same-origin',
                    headers: {
                        Accept:
                            'application/json'
                    },
                    cache: 'no-store'
                }
            );

            if (!response.ok) {
                return null;
            }

            return await response.json();
        } catch (error) {
            console.error(
                'Unable to read Shopify cart:',
                error
            );

            return null;
        }
    }

    async function applyPartnerDiscountToCart(
        code,
        note
    ) {
        const cleanCode =
            cleanStoreCode(code);

        if (!cleanCode) {
            return {
                success: false,
                cart: null
            };
        }

        removeStoredManualDiscount();

        const partnerAttributes =
            await getPartnerCartAttributes(
                cleanCode
            );

        const requestBody = {
            discount: cleanCode,
            attributes: partnerAttributes
        };

        if (
            typeof note === 'string' &&
            note.trim()
        ) {
            requestBody.note =
                note.trim();
        }

        try {
            const response = await fetch(
                getCartUpdateUrl(),
                {
                    method: 'POST',
                    credentials: 'same-origin',
                    headers: {
                        'Content-Type':
                            'application/json',
                        Accept:
                            'application/json'
                    },
                    body: JSON.stringify(
                        requestBody
                    ),
                    cache: 'no-store'
                }
            );

            if (!response.ok) {
                throw new Error(
                    `Cart update failed with status ${response.status}`
                );
            }

            const cart =
                await response.json();

            const verifiedCart =
                (await getCurrentCart()) ||
                cart;

            return {
                success:
                    cartContainsDiscount(
                        verifiedCart,
                        cleanCode
                    ),
                cart: verifiedCart
            };
        } catch (error) {
            console.error(
                'Unable to apply partner discount:',
                error
            );

            return {
                success: false,
                cart: null
            };
        }
    }

    function getCartNote() {
        const noteInput =
            document.getElementById(
                'cart-note'
            );

        return noteInput?.value || '';
    }

    function getClickableElement(target) {
        if (!(target instanceof Element)) {
            return null;
        }

        return target.closest(
            'a, button, input[type="submit"]'
        );
    }

    function isAcceleratedPaymentElement(
        element
    ) {
        return Boolean(
            element?.closest(
                '.shopify-payment-button'
            ) ||
            element?.closest(
                '[data-shopify="payment-button"]'
            ) ||
            element?.closest(
                'shopify-accelerated-checkout'
            ) ||
            element?.closest(
                'shopify-accelerated-checkout-cart'
            )
        );
    }

    function isCheckoutElement(target) {
        const element =
            getClickableElement(target);

        if (!element) return false;

        if (
            element.disabled ||
            element.getAttribute(
                'aria-disabled'
            ) === 'true'
        ) {
            return false;
        }

        if (
            isAcceleratedPaymentElement(
                element
            )
        ) {
            return false;
        }

        const href =
            element.getAttribute('href') ||
            element.dataset?.href ||
            '';

        const name =
            String(
                element.getAttribute(
                    'name'
                ) || ''
            ).toLowerCase();

        const identifier =
            String(
                element.id || ''
            ).toLowerCase();

        const className =
            typeof element.className ===
            'string'
                ? element.className.toLowerCase()
                : '';

        const text =
            String(
                element.textContent ||
                element.value ||
                ''
            )
                .trim()
                .toLowerCase();

        if (
            /\/checkout(?:[/?#]|$)/i.test(
                href
            )
        ) {
            return true;
        }

        if (name === 'checkout') {
            return true;
        }

        if (
            element.hasAttribute(
                'data-checkout'
            ) ||
            element.hasAttribute(
                'data-cart-checkout'
            )
        ) {
            return true;
        }

        if (
            identifier.includes(
                'cart-checkout'
            ) ||
            identifier === 'checkout'
        ) {
            return true;
        }

        if (
            className.includes(
                'previewcartcheckout'
            ) ||
            className.includes(
                'preview-cart-checkout'
            ) ||
            className.includes(
                'cart-checkout'
            ) ||
            className.includes(
                'checkout-button'
            )
        ) {
            return true;
        }

        return (
            text === 'checkout' ||
            text === 'proceed to checkout'
        );
    }

    function getCheckoutDestination(target) {
        const element =
            getClickableElement(target);

        const candidate =
            element?.getAttribute('href') ||
            element?.dataset?.href ||
            '';

        if (candidate) {
            try {
                const url = new URL(
                    candidate,
                    window.location.origin
                );

                if (
                    url.origin ===
                        window.location.origin &&
                    /\/checkout(?:[/?#]|$)/i.test(
                        url.pathname
                    )
                ) {
                    return (
                        `${url.pathname}` +
                        `${url.search}` +
                        `${url.hash}`
                    );
                }
            } catch (error) {
                // Use normal checkout URL.
            }
        }

        return getCheckoutUrl();
    }

    function setCheckoutLoadingState(
        target,
        loading
    ) {
        const element =
            getClickableElement(target);

        if (!element) return;

        if (loading) {
            element.setAttribute(
                'aria-busy',
                'true'
            );

            element.classList.add(
                'is-loading'
            );

            if (
                element.tagName ===
                    'BUTTON' ||
                element.tagName ===
                    'INPUT'
            ) {
                element.disabled = true;
            }
        } else {
            element.removeAttribute(
                'aria-busy'
            );

            element.classList.remove(
                'is-loading'
            );

            if (
                element.tagName ===
                    'BUTTON' ||
                element.tagName ===
                    'INPUT'
            ) {
                element.disabled = false;
            }
        }
    }

    async function continueToCheckout(
        target
    ) {
        if (checkoutStarted) return;

        const store =
            determineCurrentStore();

        const partnerCode =
            cleanStoreCode(
                store?.code
            );

        if (!partnerCode) return;

        checkoutStarted = true;

        setCheckoutLoadingState(
            target,
            true
        );

        const checkoutDestination =
            getCheckoutDestination(
                target
            );

        const result =
            await applyPartnerDiscountToCart(
                partnerCode,
                getCartNote()
            );

        if (result.success) {
            window.location.assign(
                checkoutDestination
            );

            return;
        }

        const fallbackUrl =
            `/discount/${encodeURIComponent(partnerCode)}` +
            `?redirect=${encodeURIComponent(checkoutDestination)}`;

        window.location.assign(
            fallbackUrl
        );
    }

    function handleCheckoutClick(event) {
        if (
            checkoutStarted ||
            event.defaultPrevented
        ) {
            return;
        }

        if (
            !isCheckoutElement(
                event.target
            )
        ) {
            return;
        }

        const store =
            determineCurrentStore();

        if (
            !cleanStoreCode(
                store?.code
            )
        ) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        continueToCheckout(
            event.target
        );
    }

    function handleCheckoutSubmit(event) {
        if (checkoutStarted) return;

        const submitter =
            event.submitter;

        const form =
            event.target;

        const formAction =
            form?.getAttribute?.(
                'action'
            ) || '';

        const formHasCheckout =
            /\/checkout(?:[/?#]|$)/i.test(
                formAction
            );

        const submitterIsCheckout =
            submitter &&
            isCheckoutElement(
                submitter
            );

        if (
            !formHasCheckout &&
            !submitterIsCheckout
        ) {
            return;
        }

        const store =
            determineCurrentStore();

        if (
            !cleanStoreCode(
                store?.code
            )
        ) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        continueToCheckout(
            submitter || form
        );
    }

    function installCheckoutProtection() {
        document.addEventListener(
            'click',
            handleCheckoutClick,
            true
        );

        document.addEventListener(
            'submit',
            handleCheckoutSubmit,
            true
        );
    }

    async function synchronizePartnerCart() {
        const store =
            determineCurrentStore();

        const partnerCode =
            cleanStoreCode(
                store?.code
            );

        if (!partnerCode) return;

        removeStoredManualDiscount();

        const explicitPartnerEntry =
            Boolean(
                getCodeFromPartnerPage() ||
                getCodeFromUrlParameter() ||
                getCodeFromDiscountPath()
            );

        if (
            !explicitPartnerEntry ||
            cartSyncStarted
        ) {
            return;
        }

        cartSyncStarted = true;

        await applyPartnerDiscountToCart(
            partnerCode
        );

        window.dispatchEvent(
            new CustomEvent(
                'bestypop:cart-discount-synced',
                {
                    detail: {
                        code: partnerCode
                    }
                }
            )
        );
    }

    currentStore =
        determineCurrentStore();

    window.BestyPopStore = {
        getCurrentStore() {
            const refreshed =
                determineCurrentStore();

            if (refreshed) {
                currentStore =
                    refreshed;
            }

            return currentStore
                ? { ...currentStore }
                : null;
        },

        getCurrentStoreCode() {
            return (
                this.getCurrentStore()
                    ?.code || ''
            );
        },

        setCurrentStore(code, source) {
            const store =
                saveStore(
                    code,
                    source || 'manual'
                );

            if (store) {
                removeStoredManualDiscount();
                rewriteHomepageLinks(
                    document
                );
            }

            return store;
        },

        getStorefrontHomeUrl() {
            const code =
                this.getCurrentStoreCode();

            return (
                `${window.location.origin}` +
                getPartnerHomePath(code)
            );
        },

        buildStoreUrl(destination) {
            return buildStoreUrl(
                destination,
                this.getCurrentStoreCode()
            );
        },

        buildProductShareUrl(destination) {
            return this.buildStoreUrl(
                destination
            );
        },

        async applyCurrentStoreToCart() {
            const code =
                this.getCurrentStoreCode();

            if (!code) {
                return {
                    success: false,
                    cart: null
                };
            }

            return applyPartnerDiscountToCart(
                code
            );
        },

        async getCurrentStoreIdentity() {
            const code =
                this.getCurrentStoreCode();

            if (!code) {
                return {
                    code: '',
                    name: '',
                    domain: ''
                };
            }

            const name =
                await fetchStoreName(code);

            return {
                code,
                name,
                domain:
                    derivePartnerDomain(name)
            };
        },

        clearCurrentStore() {
            deleteCookie(
                COOKIE_NAME
            );

            try {
                window.localStorage.removeItem(
                    STORAGE_KEY
                );
            } catch (error) {
                // Ignore localStorage access errors.
            }

            currentStore = null;
        }
    };

    installCheckoutProtection();

    if (
        document.readyState === 'loading'
    ) {
        document.addEventListener(
            'DOMContentLoaded',
            installPartnerHomeNavigation,
            {
                once: true
            }
        );
    } else {
        installPartnerHomeNavigation();
    }

    window.setTimeout(
        synchronizePartnerCart,
        0
    );

    window.dispatchEvent(
        new CustomEvent(
            'bestypop:store-ready',
            {
                detail: {
                    store:
                        currentStore
                            ? {
                                ...currentStore
                            }
                            : null
                }
            }
        )
    );
})();   